/**
 * Reporte: Personal por empresa — Fase 7.3 (reporte 5 de 7)
 *
 * Padrón actual de afiliados con relación laboral activa en una empresa
 * específica. La selección de empresa es obligatoria.
 *
 * Reutiliza `useRelacionesLaboralesPorEmpresa` (Fase 4.2).
 */

import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  TextField,
  Autocomplete,
  Alert,
  Divider,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import PeopleIcon from '@mui/icons-material/People';

import { useEmpresas } from '../../hooks/useEmpresas';
import { useRelacionesLaboralesPorEmpresa } from '../../hooks/useRelacionesLaborales';
import { formatDate, formatCuit } from '../../utils/format';
import type { RelacionLaboralExpandida } from '../../types/relacionLaboral';

import {
  createReportePdf,
  addReporteTable,
  addReporteFooter,
  fmtDate,
  fmtText,
  type ColumnaPdf,
} from '../../utils/reportes/pdf';
import { generarReporteExcel, type ColumnaExcel } from '../../utils/reportes/excel';
import { triggerBlobDownload, buildReporteFilename } from '../../utils/reportes/download';

// ── Tipos auxiliares ──────────────────────────────────────────────────────────

interface RowPersonal {
  id: string;
  apellido: string;
  nombre: string;
  cuil: string;
  estadoAfiliado: string;
  sucursal: string;
  categoria: string;
  categoria_codigo: string;
  fechaAlta: string;
  antiguedadDias: number;
}

interface RelacionExtendida extends RelacionLaboralExpandida {
  // El hook por-empresa trae afiliado embebido pero el tipo
  // RelacionLaboralExpandida no lo declara — lo agregamos acá.
  afiliado?: {
    cuil: string;
    apellido: string;
    nombre: string;
    estado: string;
  } | null;
}

function antiguedadEnDias(fechaAltaIso: string): number {
  const alta = new Date(fechaAltaIso);
  const hoy = new Date();
  return Math.max(0, Math.floor((hoy.getTime() - alta.getTime()) / (1000 * 60 * 60 * 24)));
}

// ── Componente ────────────────────────────────────────────────────────────────

export default function PersonalEmpresaReporte() {
  const [empresaId, setEmpresaId] = useState<string | null>(null);

  const { data: empresasResult } = useEmpresas({ pageSize: 1000 });
  const empresas = empresasResult?.data ?? [];

  const {
    data: relaciones = [],
    isLoading,
    isError,
  } = useRelacionesLaboralesPorEmpresa(empresaId ?? undefined);

  const empresa = empresas.find((e) => e.id === empresaId);

  const rows: RowPersonal[] = useMemo(
    () =>
      (relaciones as RelacionExtendida[])
        .map((rel) => ({
          id: rel.id,
          apellido: rel.afiliado?.apellido ?? '—',
          nombre: rel.afiliado?.nombre ?? '—',
          cuil: rel.afiliado?.cuil ?? '',
          estadoAfiliado: rel.afiliado?.estado ?? '—',
          sucursal: rel.sucursal?.denominacion ?? '—',
          categoria: rel.categoria?.descripcion ?? '—',
          categoria_codigo: rel.categoria?.codigo ?? '',
          fechaAlta: rel.fecha_alta,
          antiguedadDias: antiguedadEnDias(rel.fecha_alta),
        }))
        .sort((a, b) => a.apellido.localeCompare(b.apellido)),
    [relaciones],
  );

  const subtitulo = empresa
    ? `${empresa.razon_social} · CUIT ${formatCuit(empresa.cuit)} · ${rows.length} afiliados activos`
    : 'Sin empresa seleccionada';

  // ── Export PDF ─────────────────────────────────────────────────────────
  const handleExportPdf = () => {
    const doc = createReportePdf({
      titulo: 'Personal por empresa',
      subtitulo,
      orientacion: 'landscape',
    });

    const columnas: ColumnaPdf[] = [
      { header: 'Apellido', format: fmtText },
      { header: 'Nombre', format: fmtText },
      { header: 'CUIL', align: 'center', format: (v) => formatCuit(String(v)) },
      { header: 'Sucursal', format: fmtText },
      { header: 'Categoría', format: fmtText },
      { header: 'Fecha alta', align: 'center', format: fmtDate },
      { header: 'Antigüedad (días)', align: 'right', format: fmtText },
    ];

    const body = rows.map((r) => [
      r.apellido,
      r.nombre,
      r.cuil,
      r.sucursal,
      r.categoria,
      r.fechaAlta,
      r.antiguedadDias,
    ]);

    addReporteTable(doc, columnas, body, {
      titulo: 'Personal por empresa',
      subtitulo,
    });
    addReporteFooter(doc);

    const blob = doc.output('blob');
    const nombre = empresa
      ? `personal-${empresa.razon_social}`
      : 'personal-empresa';
    triggerBlobDownload(blob, buildReporteFilename(nombre, 'pdf'));
  };

  // ── Export Excel ───────────────────────────────────────────────────────
  const handleExportExcel = async () => {
    const columnas: ColumnaExcel[] = [
      { header: 'Apellido', key: 'apellido', width: 22, format: 'text' },
      { header: 'Nombre', key: 'nombre', width: 22, format: 'text' },
      { header: 'CUIL', key: 'cuil', width: 14, format: 'text' },
      { header: 'Estado afiliado', key: 'estado', width: 14, format: 'text' },
      { header: 'Sucursal', key: 'sucursal', width: 20, format: 'text' },
      { header: 'Categoría', key: 'categoria', width: 28, format: 'text' },
      { header: 'Cod. categoría', key: 'codCategoria', width: 12, format: 'text' },
      { header: 'Fecha alta', key: 'fechaAlta', width: 12, format: 'date' },
      { header: 'Antigüedad (días)', key: 'antig', width: 14, format: 'integer' },
    ];

    const rowsExcel = rows.map((r) => ({
      apellido: r.apellido,
      nombre: r.nombre,
      cuil: formatCuit(r.cuil),
      estado: r.estadoAfiliado,
      sucursal: r.sucursal,
      categoria: r.categoria,
      codCategoria: r.categoria_codigo,
      fechaAlta: new Date(r.fechaAlta),
      antig: r.antiguedadDias,
    }));

    const blob = await generarReporteExcel({
      titulo: 'Personal por empresa',
      subtitulo,
      nombreHoja: 'Personal',
      columnas,
      rows: rowsExcel,
    });
    const nombre = empresa ? `personal-${empresa.razon_social}` : 'personal-empresa';
    triggerBlobDownload(blob, buildReporteFilename(nombre, 'xlsx'));
  };

  // ── DataGrid ────────────────────────────────────────────────────────────
  const columns: GridColDef<RowPersonal>[] = [
    { field: 'apellido', headerName: 'Apellido', flex: 1, minWidth: 140 },
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 140 },
    {
      field: 'cuil',
      headerName: 'CUIL',
      width: 140,
      renderCell: (p: GridRenderCellParams<RowPersonal>) => formatCuit(p.row.cuil),
    },
    { field: 'sucursal', headerName: 'Sucursal', flex: 1, minWidth: 160 },
    { field: 'categoria', headerName: 'Categoría', flex: 1.5, minWidth: 200 },
    {
      field: 'fechaAlta',
      headerName: 'Fecha alta',
      width: 120,
      renderCell: (p: GridRenderCellParams<RowPersonal>) => formatDate(p.row.fechaAlta),
    },
    {
      field: 'antiguedadDias',
      headerName: 'Antigüedad (días)',
      width: 140,
      align: 'right',
      headerAlign: 'right',
    },
  ];

  const sinEmpresa = !empresaId;
  const sinDatos = !!empresaId && !isLoading && !isError && rows.length === 0;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to="/reportes"
        sx={{ mb: 2 }}
      >
        Volver a Reportes
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Personal por empresa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Afiliados con relación laboral activa en la empresa seleccionada.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Filtros
        </Typography>
        <Autocomplete
          sx={{ minWidth: 360 }}
          size="small"
          options={empresas}
          getOptionLabel={(opt) => opt.razon_social}
          value={empresas.find((e) => e.id === empresaId) ?? null}
          onChange={(_, val) => setEmpresaId(val?.id ?? null)}
          renderInput={(params) => (
            <TextField {...params} label="Empresa (requerida)" required />
          )}
        />
      </Card>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        mb={2}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" useFlexGap>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Empresa
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              {empresa?.razon_social ?? '—'}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Afiliados activos
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {rows.length}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPdf}
            disabled={rows.length === 0}
          >
            Descargar PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableViewIcon />}
            onClick={handleExportExcel}
            disabled={rows.length === 0}
          >
            Descargar Excel
          </Button>
        </Stack>
      </Stack>

      {sinEmpresa && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Seleccioná una empresa para ver el padrón de afiliados.
        </Alert>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar las relaciones laborales.
        </Alert>
      )}
      {sinDatos && (
        <Alert severity="info" sx={{ mb: 2 }}>
          La empresa no tiene afiliados con relación laboral activa.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
        />
      </Card>
    </Box>
  );
}
